import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { StoreProvider, useAuth, usePhase } from "./state/store";
import { SlideStateProvider } from "./state/slide-context";
import { PresenterSidebar } from "./components/PresenterSidebar";
import { usePhasePolling } from "./hooks/usePhasePolling";
import { useWinningTopicPolling } from "./hooks/useWinningTopic";
import { LoginPage } from "./pages/LoginPage";
import { MobileLoginPage } from "./pages/MobileLoginPage";
import { PresenterPage } from "./pages/PresenterPage";
import { ParticipantPage } from "./pages/ParticipantPage";
import { AdminPage } from "./pages/AdminPage";
import { ShowcasePage } from "./pages/ShowcasePage";
import { MagicLinkPage } from "./pages/MagicLinkPage";
import { ErgebnisPage } from "./pages/ErgebnisPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ReadOnlyBanner } from "./components/ReadOnlyBanner";
import { ReconnectBanner } from "./components/ReconnectBanner";
import { PrimitivesGallery } from "./dev/PrimitivesGallery";
import { VotingStage } from "./workshop/stages/VotingStage";
import { WorkingStage } from "./workshop/stages/WorkingStage";
import { EvaluatingStage } from "./workshop/stages/EvaluatingStage";
import { TopicsStage } from "./workshop/stages/TopicsStage";
import { GroupingStage } from "./workshop/stages/GroupingStage";

/**
 * Beacon-Routing (Sprint 2026-04, Variante C):
 *
 *   /                  → Login (Auto-Redirect via PhaseRouter)
 *   /login             → Login alias
 *   /m/login           → Mobile-Login (light skin)
 *   /m/vote            → Mobile-Vote
 *   /m/work            → Mobile-Workspace
 *   /m/showcase        → Mobile-Showcase
 *   /present           → Beamer Slide-Carousel (Title, QR, Winner, Overnight, Reveal)
 *   /workshop/voting   → Beamer Vote-Tally-Stage
 *   /workshop/working  → Beamer Working-Hero-Stage
 *   /workshop/evaluating → Beamer Eval-Stream-Stage
 *   /workshop/topics   → Beamer Topics-Reveal-Stage
 *   /workshop/grouping → Beamer Team-Reveal-Stage
 *   /showcase          → Top-3-Reveal (light skin)
 *   /admin             → Admin
 *   /admin/topics      → Admin alias
 *   /admin/live        → Admin alias
 *   /auth/magic        → Reviewer Magic-Link
 *   /ergebnis/:resultId → Dauerhafter Ergebnis-Link (öffentlich, kein Auth)
 *   /dev/primitives    → Dev-only Beacon-Komponentengalerie
 *
 * Workshop-Stages (`/workshop/*`) sind in PR6 noch leer und leiten zur
 * PresenterPage weiter. Mobile-Surfaces (`/m/*`) rendern aktuell die
 * existierende ParticipantPage; Detail-Refactor folgt in PR5.
 */

function Shell() {
  // Centralized polling — see hooks/POLLING.md for cadence rationale.
  usePhasePolling(true, 3000);
  useWinningTopicPolling(10000);
  const auth = useAuth();
  const location = useLocation();

  // Globale Sidebar nur für eingeloggte Presenter, und nicht auf Login-/
  // Mobile-/Reviewer-Routen. PresenterPage hat ihre eigene Sidebar (über
  // /present), wir blenden sie dort aus, um doppelt zu vermeiden.
  const showSidebar =
    auth.role === "presenter" &&
    !!auth.token &&
    location.pathname !== "/" &&
    location.pathname !== "/login" &&
    location.pathname !== "/auth/magic" &&
    !location.pathname.startsWith("/m") &&
    location.pathname !== "/present";

  return (
    <ErrorBoundary>
      <ReconnectBanner />
      <ReadOnlyBanner />
      {showSidebar ? (
        <div className="presenter-shell-grid">
          <PresenterSidebar />
          <div className="presenter-shell-main">
            <Outlet />
          </div>
        </div>
      ) : (
        <Outlet />
      )}
    </ErrorBoundary>
  );
}

function Outlet() {
  const location = useLocation();
  return (
    <Routes key={location.pathname}>
      {/* Auth */}
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/auth/magic" element={<MagicLinkPage />} />

      {/* Dauerhafter, persönlicher Ergebnis-Link — öffentlich, ohne
          Auth-Gate. Die resultId (unguessable UUID) ist die Berechtigung. */}
      <Route path="/ergebnis/:resultId" element={<ErgebnisPage />} />

      {/* Beamer (presenter) */}
      <Route
        path="/present"
        element={
          <Guarded role="presenter">
            <PresenterPage />
          </Guarded>
        }
      />

      {/* Beamer Workshop-Stages — Beacon dedicated routes. */}
      <Route
        path="/workshop/topics"
        element={
          <Guarded role="presenter">
            <TopicsStage />
          </Guarded>
        }
      />
      <Route
        path="/workshop/voting"
        element={
          <Guarded role="presenter">
            <VotingStage />
          </Guarded>
        }
      />
      <Route
        path="/workshop/grouping"
        element={
          <Guarded role="presenter">
            <GroupingStage />
          </Guarded>
        }
      />
      <Route
        path="/workshop/working"
        element={
          <Guarded role="presenter">
            <WorkingStage />
          </Guarded>
        }
      />
      <Route
        path="/workshop/evaluating"
        element={
          <Guarded role="presenter">
            <EvaluatingStage />
          </Guarded>
        }
      />

      {/* Mobile (participant) — Phase-driven content innerhalb ParticipantPage,
          PR5 baut dedizierte Inhalte pro Route. */}
      <Route path="/m/login" element={<MobileLoginPage />} />
      <Route
        path="/m"
        element={
          <Guarded role="participant">
            <ParticipantPage />
          </Guarded>
        }
      />
      <Route
        path="/m/vote"
        element={
          <Guarded role="participant">
            <ParticipantPage />
          </Guarded>
        }
      />
      <Route
        path="/m/work"
        element={
          <Guarded role="participant">
            <ParticipantPage />
          </Guarded>
        }
      />
      <Route
        path="/m/showcase"
        element={
          <Guarded role="participant">
            <ParticipantPage />
          </Guarded>
        }
      />

      {/* Legacy alias — bleibt funktional, PhaseRouter steuert ggf. um. */}
      <Route
        path="/participate"
        element={
          <Guarded role="participant">
            <ParticipantPage />
          </Guarded>
        }
      />

      {/* Showcase + Admin */}
      <Route
        path="/showcase"
        element={
          <Guarded>
            <ShowcasePage />
          </Guarded>
        }
      />
      <Route
        path="/admin"
        element={
          <Guarded role="presenter" allowReviewer={false}>
            <AdminPage />
          </Guarded>
        }
      />
      <Route path="/admin/topics" element={<Navigate to="/admin" replace />} />
      <Route path="/admin/live" element={<Navigate to="/admin" replace />} />

      {/* Dev */}
      <Route path="/dev/primitives" element={<PrimitivesGallery />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Guarded({
  children,
  role,
  allowReviewer = true,
}: {
  children: React.ReactNode;
  role?: "presenter" | "participant";
  allowReviewer?: boolean;
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const isReviewer = auth.role === "reviewer";
  useEffect(() => {
    if (!auth.token) {
      navigate("/", { replace: true });
      return;
    }
    if (isReviewer && !allowReviewer) {
      navigate("/present", { replace: true });
      return;
    }
    if (isReviewer) return;
    if (role === "presenter" && auth.role !== "presenter") {
      navigate("/m", { replace: true });
    }
  }, [auth, role, allowReviewer, isReviewer, navigate]);

  if (!auth.token) return null;
  if (isReviewer && !allowReviewer) return null;
  if (!isReviewer && role === "presenter" && auth.role !== "presenter") return null;
  return <>{children}</>;
}

export function App() {
  return (
    <StoreProvider>
      <SlideStateProvider>
        <PhaseRouter>
          <Shell />
        </PhaseRouter>
      </SlideStateProvider>
    </StoreProvider>
  );
}

/**
 * PhaseRouter — Phase-driven Auto-Routing.
 *
 * Reagiert auf Phase + Role + Login-Status:
 *   - Eingelogged + auf `/` → redirect auf rolle-passende Start-Route
 *   - Participant: phase-gebundenes Auto-Routing zu `/m/login|vote|work|showcase`
 *   - Presenter: phase-gebundenes Auto-Routing zwischen `/present` und
 *     `/workshop/<phase>` (für topics, voting, grouping, working, evaluating).
 *   - Reviewer: bleibt auf `/present` (read-only).
 */
function PhaseRouter({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const phase = usePhase();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!auth.token) return;

    const path = location.pathname;

    // Reviewer: nur einmal redirect zur Beamer-Sicht, danach freie Navigation.
    if (auth.role === "reviewer") {
      if (path === "/" || path === "/login") {
        navigate("/present", { replace: true });
      }
      return;
    }

    // Participant
    if (auth.role === "participant") {
      if (path.startsWith("/m") || path === "/participate") return;
      if (path === "/showcase" || path === "/auth/magic") return;
      // Der Ergebnis-Link ist öffentlich — er muss auch MIT Login-Token
      // erreichbar bleiben, sonst prallt genau die Person ab, die ihren
      // eigenen QR-Code mit demselben Handy öffnet.
      if (path.startsWith("/ergebnis/")) return;
      if (path === "/" || path === "/login") {
        navigate(targetForParticipant(phase), { replace: true });
        return;
      }
      // Ungültige Route für Participant → auf /m
      navigate(targetForParticipant(phase), { replace: true });
      return;
    }

    // Presenter: bleibt auf /present (PresenterWorkshopOverlay hat alle
     //  Operator-Controls — Timer starten, Phase wechseln, Sieger setzen).
     //  /workshop/* sind dedizierte Beamer-Display-Routes, der Operator
     //  navigiert sie aktiv über die Sidebar oder direkt — KEIN Auto-Routing.
    if (auth.role === "presenter") {
      if (path === "/" || path === "/login") {
        navigate("/present", { replace: true });
      }
    }
  }, [auth.token, auth.role, phase, location.pathname, navigate]);

  return <>{children}</>;
}

function targetForParticipant(phase: string): string {
  switch (phase) {
    case "voting":
      return "/m/vote";
    case "working":
      return "/m/work";
    case "showcase":
    case "evaluated":
    case "reveal":
      return "/m/showcase";
    default:
      return "/m";
  }
}

